import JsBarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { getInventoryBarcode, getInventoryPackLabel } from './inventoryApi';
import {
  buildProductPublicUrl,
  getInventoryItemId,
  getInventoryItemName,
  normalizeProductPublicBaseUrl,
} from './productPublicUrl';

function isEan13(value) {
  return /^\d{13}$/.test(String(value || '').trim());
}

function barcodeToDataUrl(code) {
  const canvas = document.createElement('canvas');
  const options = {
    displayValue: true,
    fontSize: 12,
    textMargin: 2,
    margin: 4,
    height: 42,
    width: 1.6,
    background: '#ffffff',
    lineColor: '#111111',
  };
  if (isEan13(code)) {
    JsBarcode(canvas, code, { ...options, format: 'EAN13' });
  } else {
    JsBarcode(canvas, code, { ...options, format: 'CODE128' });
  }
  return canvas.toDataURL('image/png');
}

function wrapText(doc, text, maxWidth) {
  const lines = doc.splitTextToSize(String(text || '').trim() || '—', maxWidth);
  return lines.slice(0, 2);
}

/**
 * Build a printable sticker PDF: bordered cell, product name, optional barcode,
 * QR encoding the store's public product URL from DB Website.
 *
 * @param {Array<object>} rows inventory rows
 * @param {{ productPublicBaseUrl: string }} options store Website from DB
 * @returns {Promise<{ skipped: number, filename: string, count: number }>}
 */
export async function downloadInventoryLabelsPdf(rows, options = {}) {
  const baseUrl = normalizeProductPublicBaseUrl(options.productPublicBaseUrl);
  if (!baseUrl) {
    const err = new Error(
      'Add Website under this store’s business profile → Media (social links) before printing product QR labels.'
    );
    err.skipped = 0;
    throw err;
  }

  const labels = [];
  let skipped = 0;

  for (const row of Array.isArray(rows) ? rows : []) {
    const id = getInventoryItemId(row);
    const name = getInventoryItemName(row) || 'Item';
    const productUrl = buildProductPublicUrl(row, { baseUrl });
    const code = getInventoryBarcode(row);

    if (id == null || !productUrl) {
      skipped += 1;
      continue;
    }

    let barcodeDataUrl = null;
    let qrDataUrl;
    try {
      if (code) {
        try {
          barcodeDataUrl = barcodeToDataUrl(code);
        } catch (barcodeErr) {
          console.warn('Barcode render skipped for', code, barcodeErr);
        }
      }
      qrDataUrl = await QRCode.toDataURL(productUrl, {
        width: 200,
        margin: 1,
        errorCorrectionLevel: 'M',
      });
    } catch (err) {
      console.error('Label render failed for', id, err);
      skipped += 1;
      continue;
    }

    labels.push({
      name,
      packLabel: getInventoryPackLabel(row) || '',
      code,
      productUrl,
      barcodeDataUrl,
      qrDataUrl,
    });
  }

  if (labels.length === 0) {
    const err = new Error(
      skipped > 0
        ? 'None of the selected items could be printed as labels.'
        : 'Select at least one inventory item.'
    );
    err.skipped = skipped;
    throw err;
  }

  // A4 label sheet ~ 2 columns × 5 rows of sticker-sized cells
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 8;
  const marginY = 8;
  const gapX = 4;
  const gapY = 4;
  const cols = 2;
  const rowsPerPage = 5;
  const labelW = (pageW - marginX * 2 - gapX * (cols - 1)) / cols;
  const labelH = (pageH - marginY * 2 - gapY * (rowsPerPage - 1)) / rowsPerPage;
  const perPage = cols * rowsPerPage;

  labels.forEach((label, index) => {
    if (index > 0 && index % perPage === 0) {
      doc.addPage();
    }
    const slot = index % perPage;
    const col = slot % cols;
    const row = Math.floor(slot / cols);
    const x = marginX + col * (labelW + gapX);
    const y = marginY + row * (labelH + gapY);

    doc.setDrawColor(40);
    doc.setLineWidth(0.35);
    doc.roundedRect(x, y, labelW, labelH, 1.5, 1.5, 'S');

    const pad = 3;
    const contentW = labelW - pad * 2;
    let cursorY = y + pad + 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(17, 17, 17);
    const nameLines = wrapText(doc, label.name, contentW);
    nameLines.forEach((line) => {
      doc.text(line, x + pad, cursorY);
      cursorY += 4.2;
    });

    if (label.packLabel) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      doc.text(String(label.packLabel), x + pad, cursorY);
      cursorY += 3.5;
    }

    const mediaTop = Math.max(cursorY + 1, y + 12);
    const availableH = labelH - (mediaTop - y) - pad - 2;
    const qrSize = Math.min(28, availableH, contentW * 0.4);
    const hasBarcode = Boolean(label.barcodeDataUrl);

    if (hasBarcode) {
      const barcodeW = contentW - qrSize - 3;
      const barcodeH = Math.min(18, qrSize);
      doc.addImage(
        label.barcodeDataUrl,
        'PNG',
        x + pad,
        mediaTop,
        barcodeW,
        barcodeH,
        undefined,
        'FAST'
      );
      doc.addImage(
        label.qrDataUrl,
        'PNG',
        x + pad + barcodeW + 2,
        mediaTop,
        qrSize,
        qrSize,
        undefined,
        'FAST'
      );
    } else {
      const qrX = x + pad + (contentW - qrSize) / 2;
      doc.addImage(label.qrDataUrl, 'PNG', qrX, mediaTop, qrSize, qrSize, undefined, 'FAST');
    }
  });

  const filename = `product-labels-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return { skipped, filename, count: labels.length };
}
